import React from "react";
import { useTranslation } from "react-i18next";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";
import { getBiometricIcon } from "./icons";
import usePasskey from "./usePasskey";
import { Action, Mode } from "../../types";

type Props = {
    dispatch: React.Dispatch<Action>;
    theme: "light" | "dark";
    mode: Mode;
    onSuccess: () => void;
    onRestore: (token: string) => void;
};

const PasskeyStep = ({ dispatch, theme, mode, onSuccess, onRestore }: Props) => {
    const { t } = useTranslation();
    const isLogin = mode === "login";
    const isDark = theme === "dark";
    const biometric = React.useMemo(() => getBiometricIcon(), []);
    const [faqOpen, setFaqOpen] = React.useState(false);

    const { connect, loading, error } = usePasskey({
        isLogin,
        onLink: () => {
            dispatch({ type: "PASSKEY_LINKED" });
            onSuccess();
        },
        onRestore: (token) => {
            dispatch({ type: "PASSKEY_LINKED" });
            onRestore(token);
        },
    });

    return (
        <>
            <div className="flex flex-col items-center gap-1.5">
                <span className="font-changa-one text-base text-center">
                    {t(isLogin ? "signup.passkey_restore_title" : "signup.passkey_title")}
                </span>
                <p className="text-sm text-center opacity-60 leading-relaxed">
                    {t(isLogin ? "signup.passkey_restore_description" : "signup.passkey_description")}
                </p>
            </div>

            {error && (
                <p className="text-rose-400 text-[10px] text-center">{error}</p>
            )}

            <div className="w-full mt-2">
                <ButtonHapticWrapper>
                    <button
                        type="button"
                        onClick={connect}
                        disabled={loading}
                        className={`w-full h-12 rounded-xl font-changa-one text-sm tracking-wide uppercase cursor-pointer transition-colors flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed ${
                            isDark
                                ? "bg-white text-gray-900 hover:bg-white/90"
                                : "bg-blue-900 text-white hover:bg-blue-800"
                        }`}
                    >
                        {loading ? (
                            <span className="inline-block w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                        ) : (
                            <>
                                {biometric.icon}
                                <span>{biometric.label}</span>
                            </>
                        )}
                    </button>
                </ButtonHapticWrapper>
            </div>

            <div className="w-full mt-3">
                <button
                    type="button"
                    onClick={() => setFaqOpen((open) => !open)}
                    className="w-full flex items-center justify-between gap-2 text-xs opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
                >
                    <span>{t("signup.passkey_faq_question")}</span>
                    <svg className={`w-3 h-3 transition-transform ${faqOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
                {faqOpen && (
                    <p className="text-xs opacity-40 leading-relaxed mt-2">
                        {t("signup.passkey_faq_answer")}
                    </p>
                )}
            </div>
        </>
    );
};

export default PasskeyStep;
